import { Map } from 'immutable'
import { isEmpty } from 'lodash-es'
import { createSelector } from 'reselect'

import { ELIGIBILITY_TYPES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import {
  ExtractedValues,
  OcrStatus,
  UploadStatus,
} from '@lyrahealth-inc/shared-app-logic/src/features/healthPlan/types/healthPlanCardUpload'
import {
  getHealthPlanCopayAmount as getHealthPlanCopayAmountUtil,
  isHealthPlanActive,
  isHealthPlanEligibleForCostShare,
  shouldUseCopayForCostEstimateError as shouldUseCopayForCostEstimateErrorUtil,
} from '@lyrahealth-inc/shared-app-logic/src/features/healthPlan/utils'

import { getIsCopayForMaxCostEstimateEnabled } from '../../../data/featureFlags/featureFlagSelectors'

export const getHealthPlan = (state: $TSFixMe) => state?.get('healthPlan')

export const getHealthPlanName = createSelector(getHealthPlan, (healthPlan) => healthPlan?.get('healthPlanName'))

export const getSecondaryHealthPlanName = createSelector(getHealthPlan, (healthPlan) =>
  healthPlan?.get('secondaryHealthPlanName'),
)

export const getHealthPlansList = createSelector(getHealthPlan, (healthPlan) => healthPlan?.get('healthPlansList'))

export const getHealthPlanEligibilityType = createSelector(getHealthPlan, (healthPlan) =>
  healthPlan?.get('eligibilityType', undefined),
)

export const getHealthPlanEligibilityStatus = createSelector(getHealthPlan, (healthPlan) =>
  healthPlan?.get('medicalPlanEligibilityStatus'),
)

export const getSecondaryHealthPlanEligibilityStatus = createSelector(getHealthPlan, (healthPlan) =>
  healthPlan?.get('secondaryHealthPlanMedicalPlanEligibilityStatus'),
)

export const getHealthPlanMembershipId = createSelector(getHealthPlan, (healthPlan) =>
  healthPlan?.get('healthPlanMembershipId'),
)

export const getSecondaryHealthPlanMembershipId = createSelector(getHealthPlan, (healthPlan) =>
  healthPlan?.get('secondaryHealthPlanMembershipId'),
)

export const getIsHealthPlanProvided = createSelector(getHealthPlanName, (healthPlanName) => !isEmpty(healthPlanName))

export const getIsSecondaryHealthPlanProvided = createSelector(
  getSecondaryHealthPlanMembershipId,
  (secondaryHealthPlanMembershipId) => {
    return !isEmpty(secondaryHealthPlanMembershipId)
  },
)

export const getHealthPlanPaymentCardLast4 = createSelector(getHealthPlan, (healthPlan) => healthPlan?.get('cardLast4'))

export const getHealthPlanPaymentCardBrand = createSelector(getHealthPlan, (healthPlan) => healthPlan?.get('cardBrand'))

export const getHealthPlanInfractionCardLast4 = createSelector(getHealthPlan, (healthPlan) =>
  healthPlan?.get('infractionCardLast4'),
)

export const getSelfPayCardLast4 = createSelector(getHealthPlan, (healthPlan) => healthPlan?.get('selfPayCardLast4'))

export const getSelfPayCardBrand = createSelector(getHealthPlan, (healthPlan) => healthPlan?.get('selfPayCardBrand'))

export const getIsUserDependent = createSelector(getHealthPlanEligibilityType, (healthPlanEligibilityType) => {
  return healthPlanEligibilityType === ELIGIBILITY_TYPES.DEPENDENT
})

export const getIsPatientDataFetched = createSelector(getHealthPlan, (healthPlan) =>
  healthPlan?.get('isPatientDataFetched'),
)

export const getUser = createSelector(getHealthPlan, (healthPlan) => healthPlan?.get('user'))

export const geHealthPlanUnresolvedInfractions = createSelector(getHealthPlan, (healthPlan: any) =>
  healthPlan?.get('unresolvedInfractions'),
)

export const getUsername = createSelector(getUser, (user) => user?.get('username'))

export const getUsedSessionCount = createSelector(getHealthPlan, (healthPlan) => healthPlan?.get('usedSessionCount'))

export const getUsedSessionCountByProgram = createSelector(getHealthPlan, (healthPlan) =>
  healthPlan?.get('usedSessionCountByProgram')?.toJS(),
)

export const getSelectedHealthPlanConfig = createSelector(
  getHealthPlanName,
  getHealthPlansList,
  (healthPlanName, healthPlansList) =>
    healthPlansList?.find((healthPlan: Map<string, any>) => healthPlan.get('eligibility_name') === healthPlanName),
)

export const getHealthPlanDisplayName = createSelector(getSelectedHealthPlanConfig, (selectedHealthPlanConfig) =>
  selectedHealthPlanConfig?.get('name'),
)

export const getIsHealthPlanActive = createSelector(
  [getHealthPlanEligibilityStatus, getSecondaryHealthPlanEligibilityStatus],
  (eligibilityStatus, secondaryEligibilityStatus) =>
    isHealthPlanActive({
      healthPlanEligibilityStatus: eligibilityStatus,
      secondaryHealthPlanEligibilityStatus: secondaryEligibilityStatus,
    }),
)

export const getIsHealthPlanEligibleForCostShare = createSelector(
  [getHealthPlanEligibilityStatus, getSecondaryHealthPlanEligibilityStatus],
  (eligibilityStatus, secondaryEligibilityStatus) =>
    isHealthPlanEligibleForCostShare({
      healthPlanEligibilityStatus: eligibilityStatus,
      secondaryHealthPlanEligibilityStatus: secondaryEligibilityStatus,
    }),
)

export const getIsPaymentInfoConfirmed = createSelector([getHealthPlanPaymentCardLast4], (cardLast4) => !!cardLast4)

export const getIsInfractionPaymentInfoConfirmed = createSelector(
  [getHealthPlanInfractionCardLast4],
  (cardLast4) => !!cardLast4,
)

export const getIsSelfPayPaymentInfoConfirmed = createSelector([getSelfPayCardLast4], (cardLast4) => !!cardLast4)

export const getHealthPlanCopayAmount = createSelector([getSelectedHealthPlanConfig], (selectedHealthPlanConfig) =>
  getHealthPlanCopayAmountUtil(selectedHealthPlanConfig?.toJS()),
)

export const getShouldUseCopayForCostEstimateError = createSelector(
  [getSelectedHealthPlanConfig, getIsCopayForMaxCostEstimateEnabled],
  (selectedHealthPlanConfig, isCopayForMaxCostEstimateEnabled) =>
    shouldUseCopayForCostEstimateErrorUtil(selectedHealthPlanConfig?.toJS(), isCopayForMaxCostEstimateEnabled),
)

export const getIsHealthPlanIntegrated = createSelector(getHealthPlan, (healthPlan) =>
  healthPlan?.get('isHealthPlanIntegrated'),
)

export const getIsSecondaryHealthPlanIntegrated = createSelector(getHealthPlan, (healthPlan) =>
  healthPlan?.get('isSecondaryHealthPlanIntegrated'),
)

export const getHasExistingHealthPlan = createSelector(
  [getHealthPlanEligibilityStatus, getSecondaryHealthPlanEligibilityStatus],
  (eligibilityStatus, secondaryEligibilityStatus) => !!eligibilityStatus || !!secondaryEligibilityStatus,
)

export const getLastEligibilityCheckDate = createSelector(getHealthPlan, (healthPlan) =>
  healthPlan?.get('lastEligibilityCheckDate'),
)

export const getHealthPlanLegalInfo = createSelector(getHealthPlan, (healthPlan) => {
  return {
    legalFirstName: healthPlan?.get('legalFirstName'),
    legalLastName: healthPlan?.get('legalLastName'),
    legalDob: healthPlan?.get('legalDob'),
    healthPlanName: healthPlan?.get('healthPlanName'),
    healthPlanMembershipId: healthPlan?.get('healthPlanMembershipId'),
  }
})

export const getSecondaryHealthPlanLegalInfo = createSelector(getHealthPlan, (healthPlan) => {
  return {
    legalFirstName: healthPlan?.get('secondaryHealthPlanLegalFirstName'),
    legalLastName: healthPlan?.get('secondaryHealthPlanLegalLastName'),
    legalDob: healthPlan?.get('secondaryHealthPlanLegalDob'),
    healthPlanName: healthPlan?.get('secondaryHealthPlanName'),
    healthPlanMembershipId: healthPlan?.get('secondaryHealthPlanMembershipId'),
  }
})

// Health Plan Card Upload OCR selectors
export const getCardUploadState = createSelector(getHealthPlan, (healthPlan) => healthPlan?.get('cardUpload'))

export const getFrontCardUploadState = createSelector(getCardUploadState, (cardUpload) =>
  cardUpload?.get('frontCard')?.toJS(),
)

export const getBackCardUploadState = createSelector(getCardUploadState, (cardUpload) =>
  cardUpload?.get('backCard')?.toJS(),
)

export const getFrontCardExtractedValues = createSelector(
  getFrontCardUploadState,
  (frontCard): ExtractedValues | null => frontCard?.extractedValues ?? null,
)

export const getIsFrontCardProcessed = createSelector(getFrontCardUploadState, (frontCard) => {
  const status = frontCard?.status
  return status === OcrStatus.OCR_COMPLETED || status === OcrStatus.OCR_FAILED
})

export const getIsBackCardProcessed = createSelector(getBackCardUploadState, (backCard) => {
  const status = backCard?.status
  return (
    status === UploadStatus.UPLOADED ||
    status === UploadStatus.UPLOAD_FAILED ||
    status === OcrStatus.OCR_COMPLETED ||
    status === OcrStatus.OCR_FAILED
  )
})
